import { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../services/api';

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCount = async () => {
      try { const res = await notificationAPI.unreadCount(); setCount(res.data?.count || 0); }
      catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleOpen = async () => {
    setOpen(!open);
    if (!open) {
      try {
        const res = await notificationAPI.list();
        setNotifications(res.data?.results || res.data || []);
      } catch {}
    }
  };

  const handleMarkAllRead = async () => {
    try { await notificationAPI.markAllRead(); setCount(0); setNotifications(prev => prev.map(n => ({ ...n, is_read: true }))); }
    catch {}
  };

  const handleNotificationClick = async (n) => {
    // Mark individual notification as read
    try { await notificationAPI.markRead(n.id); setCount(c => Math.max(0, c - 1)); setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x)); }
    catch {}
    // Navigate to ticket if it has a related ticket
    if (n.related_ticket_type && n.related_ticket_id) {
      const typeMap = { incident: 'incident', sr: 'sr', cr: 'cr' };
      const tt = typeMap[n.related_ticket_type] || n.related_ticket_type;
      navigate(`/ticket/${tt}/${n.related_ticket_id}`);
      setOpen(false);
    }
  };

  const typeColors = {
    ticket_created: 'bg-blue-100 text-blue-700', ticket_assigned: 'bg-sky-100 text-sky-700',
    sla_warning: 'bg-amber-100 text-amber-700', sla_breach: 'bg-rose-100 text-rose-700',
    escalation: 'bg-orange-100 text-orange-700', approval_required: 'bg-purple-100 text-purple-700',
    comment_added: 'bg-emerald-100 text-emerald-700', ticket_updated: 'bg-indigo-100 text-indigo-700',
  };

  const typeLabel = {
    comment_added: '💬 Comment', ticket_created: 'Created', ticket_assigned: 'Assigned',
    approval_required: 'Approval', ticket_updated: 'Updated', sla_breach: 'SLA Breach',
    escalation: 'Escalation',
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={handleOpen} className="relative p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-slate-500 dark:text-slate-400" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-96 bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-2xl z-50 max-h-[480px] overflow-hidden flex flex-col" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b dark:border-slate-700 flex-shrink-0">
            <h3 className="font-semibold text-sm dark:text-white">Notifications {count > 0 && <span className="ml-1 text-xs bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full">{count} new</span>}</h3>
            {count > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-sky-600 hover:text-sky-800">Mark all read</button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No notifications</p>
            ) : notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`w-full text-left px-4 py-3 border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${n.is_read ? 'opacity-60' : 'bg-white dark:bg-slate-800'}`}
              >
                <div className="flex items-start gap-2.5">
                  {!n.is_read && <div className="w-2 h-2 bg-sky-500 rounded-full mt-1.5 flex-shrink-0" />}
                  {n.is_read && <div className="w-2 h-2 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`${typeColors[n.type] || 'bg-slate-100 text-slate-700'} px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap`}>
                        {typeLabel[n.type] || n.type?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className={`text-sm ${n.is_read ? 'text-slate-500 dark:text-slate-400' : 'font-medium text-slate-800 dark:text-white'} truncate`}>{n.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                  </div>
                  {n.related_ticket_type && <span className="text-slate-300 dark:text-slate-600 text-xs mt-1">→</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
